import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  Card,
  Button,
  Text,
  LoadingSpinner,
  Badge,
  tokens,
} from '../design-system';
import { FaGithub, FaLink } from 'react-icons/fa';
import apiClient from '../services/unifiedApiClient';

const RepositorySelector = ({ value, onChange }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [repos, setRepos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [urlError, setUrlError] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { canUseGitHub } = useSubscription();
  const { isDark } = useTheme();

  // Check GitHub connection status (only for paid tiers)
  useEffect(() => {
    const checkConnection = async () => {
      if (!isAuthenticated() || !canUseGitHub()) return;

      try {
        const response = await apiClient.get('/github/status');
        setIsConnected(response.connected || false);
      } catch (error) {
        console.error('Failed to check GitHub connection:', error);
        setIsConnected(false);
      }
    };

    checkConnection();
  }, [isAuthenticated, canUseGitHub]);

  // Auto-open dropdown when returning from GitHub App install
  useEffect(() => {
    if (searchParams.get('github_connected') === 'true' && isConnected) {
      setShowDropdown(true);
      // Clean up the query param
      searchParams.delete('github_connected');
      setSearchParams(searchParams, { replace: true });
    }
  }, [isConnected, searchParams, setSearchParams]);

  // Fetch repositories
  const fetchRepos = async () => {
    if (!isConnected) return;

    setLoading(true);
    try {
      const response = await apiClient.get('/github/user/repos');
      setRepos(response || []);
    } catch (error) {
      console.error('Failed to fetch repositories:', error);
      setRepos([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on connect
  useEffect(() => {
    fetchRepos();
  }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refetch when user returns to this tab (e.g. after managing access on GitHub)
  useEffect(() => {
    if (!isConnected) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchRepos();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isConnected]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter repositories based on search
  const filteredRepos = repos.filter(repo =>
    repo.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (repo.description && repo.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Group repos by owner/account for display
  const groupedRepos = filteredRepos.reduce((groups, repo) => {
    const owner = repo.owner?.login || repo.owner || 'Unknown';
    if (!groups[owner]) groups[owner] = [];
    groups[owner].push(repo);
    return groups;
  }, {});
  const ownerGroups = Object.entries(groupedRepos);

  const handleSelectRepo = (repo) => {
    onChange({
      type: 'github',
      url: repo.html_url || `https://github.com/${repo.full_name}`,  // Use html_url or construct it
      branch: repo.default_branch || 'main',
      name: repo.full_name
    });
    setShowDropdown(false);
    setSearchQuery('');
  };

  // Parse "owner/repo" from various input formats
  const parseRepoInput = (input) => {
    const trimmed = input.trim().replace(/\/+$/, '');
    // Full URL: https://github.com/owner/repo or http://github.com/owner/repo(.git)
    const urlMatch = trimmed.match(/^https?:\/\/github\.com\/([^/]+)\/([^/]+?)(?:\.git)?$/);
    if (urlMatch) return { owner: urlMatch[1], repo: urlMatch[2] };
    // owner/repo format
    const slashMatch = trimmed.match(/^([a-zA-Z0-9_.-]+)\/([a-zA-Z0-9_.-]+)$/);
    if (slashMatch) return { owner: slashMatch[1], repo: slashMatch[2] };
    return null;
  };

  const handleCustomUrl = async () => {
    if (!customUrl) return;
    setUrlError('');

    const parsed = parseRepoInput(customUrl);
    if (!parsed) {
      setUrlError('Enter a repository as owner/repo (e.g. facebook/react)');
      return;
    }

    setLoading(true);
    try {
      // Verify the repo exists and is accessible
      const res = await fetch(`https://api.github.com/repos/${parsed.owner}/${parsed.repo}`, {
        headers: { 'Accept': 'application/vnd.github.v3+json' },
      });
      if (res.status === 404 || res.status === 403) {
        setUrlError(
          canUseGitHub()
            ? 'Repository not found or is private. Connect GitHub above to access private repos.'
            : 'Repository not found or is private. Upgrade to connect private repos.'
        );
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setUrlError('Could not verify repository. Please try again.');
        setLoading(false);
        return;
      }
      const repoData = await res.json();

      onChange({
        type: 'custom',
        url: repoData.html_url,
        branch: repoData.default_branch || 'main',
        name: repoData.full_name,
      });
      setShowCustomInput(false);
      setCustomUrl('');
      setUrlError('');
    } catch {
      setUrlError('Could not reach GitHub. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    onChange(null);
    setSearchQuery('');
    setCustomUrl('');
    setShowCustomInput(false);
  };

  // Install GitHub App — navigates to GitHub, returns to this page after install
  const handleInstallApp = async () => {
    setInstalling(true);
    try {
      // Capture full path + query so we can restore page state after install
      const returnTo = window.location.pathname + window.location.search;
      const data = await apiClient.get('/github/app/install-url', {
        params: { return_to: returnTo },
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
    } catch (error) {
      console.warn('GitHub App not available:', error);
    }
    setInstalling(false);
  };

  // If value is already selected, show it (must be checked before connection prompts)
  if (value) {
    return (
      <div style={{
        padding: tokens.spacing[3],
        borderRadius: tokens.borderRadius.lg,
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: tokens.spacing[1],
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
          <FaGithub size={18} style={{ color: 'var(--color-text)' }} />
          <div>
            <Text weight="medium" style={{ margin: 0, color: 'var(--color-text)' }}>
              {value.name || value.repo_name}
            </Text>
            {value.type === 'custom' && (
              <Badge size="sm" variant="secondary">Public URL</Badge>
            )}
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={handleClear}
        >
          Change
        </Button>
      </div>
    );
  }

  // Free tier: compact URL input with upgrade nudge
  if (!isConnected && !canUseGitHub()) {
    return (
      <div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: tokens.spacing[2],
        }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <input
              type="text"
              placeholder="owner/repo"
              value={customUrl}
              onChange={(e) => { setCustomUrl(e.target.value); setUrlError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomUrl(); } }}
              style={{
                width: '100%',
                padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                paddingLeft: '36px',
                border: `1px solid ${urlError ? tokens.colors.error[500] : 'var(--color-border)'}`,
                borderRadius: tokens.borderRadius.md,
                backgroundColor: 'var(--color-surface)',
                color: 'var(--color-text)',
                fontSize: tokens.typography.fontSize.sm[0],
              }}
            />
            <FaGithub
              size={16}
              style={{
                position: 'absolute',
                left: tokens.spacing[3],
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--color-text-muted)',
              }}
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={handleCustomUrl}
            disabled={!customUrl || loading}
          >
            {loading ? <LoadingSpinner size="sm" /> : 'Add'}
          </Button>
        </div>
        {urlError ? (
          <Text size="xs" style={{ color: tokens.colors.error[500], margin: `${tokens.spacing[1]} 0 0 0` }}>
            {urlError}
          </Text>
        ) : (
          <Text size="xs" style={{ color: 'var(--color-text-muted)', margin: `${tokens.spacing[1]} 0 0 0` }}>
            Public repos only.{' '}
            <a href="/pricing" style={{ color: tokens.colors.primary[500] }}>
              Upgrade
            </a>
            {' '}to connect private repos.
          </Text>
        )}
      </div>
    );
  }

  // Paid tier, not connected: show connect prompt
  if (!isConnected) {
    return (
      <div style={{
        padding: tokens.spacing[4],
        borderRadius: tokens.borderRadius.lg,
        backgroundColor: isDark ? `${tokens.colors.warning[500]}15` : tokens.colors.warning[50],
        border: `1px solid ${isDark ? `${tokens.colors.warning[500]}40` : tokens.colors.warning[200]}`,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.spacing[3],
          padding: `${tokens.spacing[2]} 0`,
        }}>
          <FaGithub size={28} style={{ color: 'var(--color-text-muted)' }} />
          <div style={{ textAlign: 'center' }}>
            <Text weight="semibold" style={{ margin: 0 }}>Connect a GitHub repository</Text>
            <Text size="sm" style={{ color: 'var(--color-text-muted)', margin: `${tokens.spacing[1]} 0 0 0` }}>
              We'll analyze your codebase to generate better plans and detect what's already built.
            </Text>
          </div>
          <Button
            type="button"
            size="md"
            onClick={handleInstallApp}
            disabled={installing}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: tokens.spacing[2],
              backgroundColor: '#24292f',
              color: '#ffffff',
              border: '1px solid rgba(255,255,255,0.15)',
            }}
          >
            {installing ? <LoadingSpinner size="sm" /> : <FaGithub size={16} />}
            Connect GitHub
          </Button>
          {!showCustomInput ? (
            <button
              type="button"
              onClick={() => setShowCustomInput(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
                fontSize: tokens.typography.fontSize.sm[0],
                padding: 0,
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
              }}
            >
              or paste a public repository URL
            </button>
          ) : (
            <div style={{ width: '100%' }}>
              <input
                type="text"
                placeholder="owner/repo"
                value={customUrl}
                onChange={(e) => { setCustomUrl(e.target.value); setUrlError(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomUrl(); } }}
                style={{
                  width: '100%',
                  padding: tokens.spacing[2],
                  border: '1px solid var(--color-border)',
                  borderRadius: tokens.borderRadius.md,
                  backgroundColor: 'var(--color-surface)',
                  color: 'var(--color-text)',
                  fontSize: tokens.typography.fontSize.sm[0],
                }}
              />
              <div style={{
                display: 'flex',
                gap: tokens.spacing[2],
                marginTop: tokens.spacing[2],
                justifyContent: 'center',
              }}>
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={handleCustomUrl}
                  disabled={!customUrl || loading}
                >
                  {loading ? <LoadingSpinner size="sm" /> : 'Add Repository'}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setShowCustomInput(false);
                    setCustomUrl('');
                    setUrlError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
              {urlError && (
                <Text size="xs" style={{ color: tokens.colors.error[500], margin: `${tokens.spacing[1]} 0 0 0` }}>
                  {urlError}
                </Text>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Repository selector (connected, no value selected)
  return (
    <div style={{ position: 'relative' }}>
      <div style={{
        display: 'flex',
        gap: tokens.spacing[2],
      }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <input
            type="text"
            placeholder={loading ? "Loading repositories..." : "Search your repositories..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowDropdown(true)}
            disabled={loading}
            style={{
              width: '100%',
              padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
              paddingLeft: '36px',
              border: '1px solid var(--color-border)',
              borderRadius: tokens.borderRadius.md,
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontSize: tokens.typography.fontSize.sm[0],
            }}
          />
          <FaGithub
            size={16}
            style={{
              position: 'absolute',
              left: tokens.spacing[3],
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)',
            }}
          />
          
          {/* Dropdown */}
          {showDropdown && !loading && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: tokens.spacing[1],
              maxHeight: '300px',
              overflowY: 'auto',
              backgroundColor: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: tokens.borderRadius.md,
              boxShadow: tokens.boxShadow.lg,
              zIndex: 100,
            }}>
              {filteredRepos.length > 0 ? (
                ownerGroups.map(([owner, ownerRepos]) => (
                  <div key={owner}>
                    {ownerGroups.length > 1 && (
                      <div style={{
                        padding: `${tokens.spacing[1]} ${tokens.spacing[3]}`,
                        backgroundColor: 'var(--color-surface-hover)',
                        borderBottom: '1px solid var(--color-border)',
                      }}>
                        <Text size="xs" weight="semibold" style={{ color: 'var(--color-text-muted)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                          {owner}
                        </Text>
                      </div>
                    )}
                    {ownerRepos.map((repo) => (
                      <button
                        key={repo.id}
                        type="button"
                        onClick={() => handleSelectRepo(repo)}
                        style={{
                          display: 'block',
                          width: '100%',
                          padding: tokens.spacing[3],
                          textAlign: 'left',
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderBottom: '1px solid var(--color-border)',
                          cursor: 'pointer',
                          transition: tokens.transitions.colors,
                          color: 'var(--color-text)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = 'var(--color-surface-hover)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <Text weight="medium" style={{ margin: 0, color: 'var(--color-text)' }}>
                            {repo.name}
                          </Text>
                          <Text size="xs" style={{ color: 'var(--color-text-muted)', margin: 0 }}>
                            {repo.private && 'Private'}{repo.private && repo.language && ' · '}{repo.language}
                          </Text>
                          {repo.description && (
                            <Text size="xs" style={{
                              color: 'var(--color-text-muted)',
                              margin: `${tokens.spacing[1]} 0 0 0`,
                            }}>
                              {repo.description}
                            </Text>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                ))
              ) : (
                <div style={{ padding: tokens.spacing[4], textAlign: 'center' }}>
                  <Text size="sm" style={{ color: 'var(--color-text-muted)' }}>
                    {searchQuery ? 'No matching repositories found' : 'No repositories found'}
                  </Text>
                </div>
              )}
              
              {/* Add custom URL option */}
              <button
                type="button"
                onClick={() => {
                  setShowDropdown(false);
                  setShowCustomInput(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: tokens.spacing[2],
                  width: '100%',
                  padding: tokens.spacing[3],
                  backgroundColor: isDark ? `${tokens.colors.primary[500]}15` : tokens.colors.primary[50],
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text)',
                }}
              >
                <FaLink size={14} />
                <Text weight="medium" style={{ margin: 0 }}>
                  Use Public Repository URL
                </Text>
              </button>

              {/* Manage repo access */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    const data = await apiClient.get('/github/app/configure-url');
                    if (data?.url) {
                      window.open(data.url, '_blank');
                    }
                  } catch (e) {
                    // No app installation — send to install
                    handleInstallApp();
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: tokens.spacing[1],
                  width: '100%',
                  padding: `${tokens.spacing[2]} ${tokens.spacing[3]}`,
                  backgroundColor: 'transparent',
                  border: 'none',
                  borderTop: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  fontSize: tokens.typography.fontSize.xs[0],
                }}
              >
                Don't see your repo? Manage repository access
              </button>
            </div>
          )}
        </div>

        {loading && <LoadingSpinner size="sm" />}
      </div>

      {/* Custom URL input */}
      {showCustomInput && (
        <div style={{ marginTop: tokens.spacing[3] }}>
          <input
            type="text"
            placeholder="owner/repo"
            value={customUrl}
            onChange={(e) => { setCustomUrl(e.target.value); setUrlError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleCustomUrl(); } }}
            style={{
              width: '100%',
              padding: tokens.spacing[2],
              border: '1px solid var(--color-border)',
              borderRadius: tokens.borderRadius.md,
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontSize: tokens.typography.fontSize.sm[0],
            }}
          />
          <div style={{ 
            display: 'flex', 
            gap: tokens.spacing[2], 
            marginTop: tokens.spacing[2] 
          }}>
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={handleCustomUrl}
              disabled={!customUrl || loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Add Repository'}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                setShowCustomInput(false);
                setCustomUrl('');
                setUrlError('');
              }}
            >
              Cancel
            </Button>
          </div>
          {urlError && (
            <Text size="xs" style={{ color: tokens.colors.error[500], margin: `${tokens.spacing[1]} 0 0 0` }}>
              {urlError}
            </Text>
          )}
        </div>
      )}

      {/* Click outside to close dropdown */}
      {showDropdown && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99,
          }}
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default RepositorySelector;